import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Snackbar,
  CircularProgress,
  Button,
  Box,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import FormPreInscricao from '../../components/PreInscricoes/FormPreInscricao';
import { criarPreInscricao } from '../../services/preInscricaoService';
import { obterEstudantePorUsuarioId } from '../../services/estudanteService';
import { listarCampanhas } from '../../services/campanhaService';
import { listarPeriodos } from '../../services/periodoService';
import { listarClasses } from '../../services/classeService';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const CriarPreInscricaoPage = () => {
  const [estudante, setEstudante] = useState(null);
  const [campanhas, setCampanhas] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [periodos, setPeriodos] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const campanhaId = searchParams.get('campanhaId');
  const { user } = useAuth();

  const [initialValues, setInitialValues] = useState({});

  // Função para buscar os dados necessários para criar uma pré-inscrição
  const fetchData = useCallback(async () => {
    console.log('CriarPreInscricaoPage - Iniciando fetchData');
    setLoading(true);
    setError('');
    try {
      const estudanteData = await obterEstudantePorUsuarioId(user.id);
      setEstudante(estudanteData);
  
      let campanhaData = null;
      if (campanhaId) {
        const response = await api.get(`/campanhas/${campanhaId}`);
        campanhaData = response.data;
        console.log('Dados completos da campanha:', campanhaData);
        console.log('Tipo da instituição:', campanhaData.instituicao.tipo);
  
        setInitialValues({
          campanhaId: campanhaData.id,
          instituicaoId: campanhaData.instituicao.id,
          instituicaoNome: campanhaData.instituicao.nome,
          campanhaNome: campanhaData.nome,
        });
  
        setCampanhas([campanhaData]);
  
        // Debug do tipo de instituição
        if (campanhaData.instituicao.tipo === 'Colégio' || 
            campanhaData.instituicao.tipo === 'Colegio' || 
            campanhaData.nome.includes('COLEGIO')) {
          console.log('Detectado tipo Colégio - Buscando classes...');
          const classesData = await listarClasses();
          console.log('Classes retornadas:', classesData);
          
          if (classesData && classesData.length > 0) {
            setClasses(classesData.map(classe => ({
              ...classe,
              numero: classe.numero.toString(),
            })));
          } else {
            console.warn('Nenhuma classe encontrada');
          }
        } else {
          console.log('Instituição não é do tipo Colégio - Buscando cursos...');
          const cursosResponse = await api.get(`/cursos`, {
            params: { instituicaoId: campanhaData.instituicao.id }
          });
          
          if (cursosResponse.data.length === 0) {
            console.warn('Nenhum curso encontrado para a instituição:', campanhaData.instituicao.id);
          } else {
            setCursos(cursosResponse.data);
          }
        }
      } else {
        const campanhasData = await listarCampanhas();
        setCampanhas(campanhasData);
      }
  
      const periodosData = await listarPeriodos();
      setPeriodos(periodosData);
      
    } catch (error) {
      console.error('CriarPreInscricaoPage - Erro ao buscar dados:', error);
      setError('Erro ao carregar dados. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [user.id, campanhaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location.key]);

  useEffect(() => {
    if (user.tipo !== 'estudante') {
      setSnackbarMessage('Apenas estudantes podem criar pré-inscrições.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (formData) => {
    console.log('CriarPreInscricaoPage - handleSubmit chamado com:', formData);
    setLoading(true);
    try {
      const response = await criarPreInscricao({
        ...formData,
        estudanteId: estudante.id
      });
      console.log('CriarPreInscricaoPage - Resposta da criação da pré-inscrição:', response);
      setSnackbarMessage('Pré-inscrição criada com sucesso!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      // Navegar para a página de comprovativo com os dados da pré-inscrição
      navigate(`/pre-inscricao-comprovativo/${response.id}`, { state: { preInscricao: response } });
    } catch (error) {
      console.error('CriarPreInscricaoPage - Erro ao criar pré-inscrição:', error);
      setSnackbarMessage('Erro ao criar pré-inscrição. Por favor, tente novamente.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
        <Button variant="contained" onClick={() => fetchData()} sx={{ mt: 2 }}>
          Tentar Novamente
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>
        Criar Nova Pré-Inscrição
      </Typography>
      <FormPreInscricao
        onSubmit={handleSubmit}
        campanhas={campanhas}
        cursos={cursos}
        periodos={periodos}
        classes={classes}
        initialValues={initialValues}
        isSubmitting={loading}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          Cancelar
        </Button>
      </Box>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
};

export default CriarPreInscricaoPage;
